import React from 'react';

/**
 * Editorial hero banner with a visible image panel on the right.
 *
 * The content (children) is rendered on top of the whole hero; caller is
 * responsible for constraining its own width (usually a Container with
 * lg:col-span-7 for text so the image on the right stays visible).
 *
 * Props:
 *  - image: background image URL (served from /public/ for reliability)
 *  - height: tailwind className for min-height
 *  - children: hero content
 *  - variant: 'light' (default) | 'dark'
 */
export default function HeroBanner({
  image,
  height,
  children,
  variant = 'light',
}) {
  const dark = variant === 'dark';
  const minH = height || 'min-h-[460px] lg:min-h-[560px]';

  const baseColor = dark ? '#14101A' : '#FAF7F5';
  const baseRGB = dark ? '20, 16, 26' : '250, 247, 245';

  return (
    <section className={`relative overflow-hidden ${minH}`} style={{ backgroundColor: baseColor }}>
      {/* Image panel — right 50% on desktop, top-right on mobile */}
      <div className="absolute inset-y-0 right-0 w-full lg:w-[58%] pointer-events-none">
        <img
          src={image}
          alt=""
          className="absolute inset-0 w-full h-full object-cover erbity-hero-drift"
          style={{ filter: dark ? 'saturate(0.9) brightness(0.72)' : 'saturate(0.95) brightness(0.94) contrast(0.96)' }}
        />
        {/* Left-side fade so the image blends into the panel color (wider soft gradient) */}
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(90deg, rgba(${baseRGB},1) 0%, rgba(${baseRGB},0.98) 8%, rgba(${baseRGB},0.85) 22%, rgba(${baseRGB},0.55) 38%, rgba(${baseRGB},0.25) 55%, rgba(${baseRGB},0.05) 78%, rgba(${baseRGB},0) 100%)`,
          }}
        />
        {/* Top fade — keep navbar area readable */}
        <div
          className="absolute inset-x-0 top-0 h-24"
          style={{
            background: `linear-gradient(180deg, rgba(${baseRGB},0.85) 0%, rgba(${baseRGB},0.5) 50%, rgba(${baseRGB},0) 100%)`,
          }}
        />
        {/* Bottom fade */}
        <div
          className="absolute inset-x-0 bottom-0 h-40"
          style={{
            background: `linear-gradient(180deg, rgba(${baseRGB},0) 0%, rgba(${baseRGB},1) 100%)`,
          }}
        />
        {/* Pink brand wash */}
        <div
          className="absolute inset-0 opacity-70"
          style={{
            background:
              'radial-gradient(55% 55% at 70% 40%, rgba(244,164,187,0.32) 0%, rgba(244,164,187,0) 65%), radial-gradient(45% 45% at 88% 88%, rgba(196,80,111,0.28) 0%, rgba(196,80,111,0) 60%)',
          }}
        />
      </div>

      {/* Mobile: image is behind everything with a stronger overall veil so text remains readable */}
      <div
        className="absolute inset-0 lg:hidden"
        style={{
          background: `linear-gradient(180deg, rgba(${baseRGB},0.92) 0%, rgba(${baseRGB},0.85) 60%, rgba(${baseRGB},1) 100%)`,
        }}
      />

      {/* Subtle dot texture across left panel */}
      <div
        className={`absolute inset-y-0 left-0 w-full lg:w-[55%] pointer-events-none ${dark ? 'opacity-[0.1]' : 'opacity-[0.04]'}`}
        style={{
          backgroundImage: `radial-gradient(${dark ? '#E7A9B9' : '#1A1420'} 1px, transparent 1px)`,
          backgroundSize: '24px 24px',
        }}
      />

      {/* Animated accent shapes */}
      <div
        className="absolute -top-24 left-1/2 lg:left-[35%] w-[420px] h-[420px] rounded-full blur-3xl opacity-30 pointer-events-none erbity-hero-pulse"
        style={{ background: 'radial-gradient(closest-side, #E7A9B9, transparent 70%)' }}
      />
      <div
        className="absolute -bottom-24 right-8 w-[360px] h-[360px] rounded-full blur-3xl opacity-25 pointer-events-none erbity-hero-pulse-slow"
        style={{ background: 'radial-gradient(closest-side, #C4506F, transparent 70%)' }}
      />

      {/* Content */}
      <div className="relative z-10">{children}</div>
    </section>
  );
}
